import React, { useRef, useState, useEffect } from 'react';
import { FaPlay, FaPause, FaVolumeMute, FaVolumeUp, FaHeart, FaMoon, FaShare } from 'react-icons/fa';
import { usePlayerStore } from '../../stores/playerStore';
import { useTheme } from '../../contexts/ThemeContext';
import { useToast } from '../../contexts/ToastContext';
import { useFavorites } from '../../contexts/FavoritesContext';
import SleepTimer from '../SleepTimer';
import ShareModal from '../ShareModal';
import MobilePlayer from './MobilePlayer';
import { useListeningStats } from '../../hooks/useListeningStats';
import { externalListenUrl } from '../../utils/streamUrl';
import { useSleepCountdown } from '../../hooks/useSleepCountdown';

const FALLBACK_IMAGE = 'https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?q=80&w=400&auto=format';

const MobilePlayerBar: React.FC = () => {
  const volumeSliderRef = useRef<HTMLDivElement>(null);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const [showSleepTimer, setShowSleepTimer] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const {
    currentStation,
    isPlaying,
    volume,
    setIsPlaying,
    setVolume,
    sleepTimer,
    isBuffering,
    isConnected,
    connectionQuality,
    streamError,
    unplayableReason,
  } = usePlayerStore();
  // Playback itself is driven by <MobilePlayer /> below, which owns the engine
  // binding; this bar only renders state.
  const externalUrl = currentStation ? externalListenUrl(currentStation) : null;
  const sleepCountdown = useSleepCountdown();
  const { addFavorite, removeFavorite, isFavorite } = useFavorites();
  const { showToast } = useToast();
  const { mode } = useTheme();
  const isFairyTheme = mode === 'fairy';
  const isNeonTheme = mode === 'neon';

  // Initialize listening stats tracking
  useListeningStats();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        volumeSliderRef.current &&
        !volumeSliderRef.current.contains(event.target as Node)
      ) {
        setShowVolumeSlider(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
  };

  const handleVolumeClick = () => {
    setShowVolumeSlider(!showVolumeSlider);
  };

  const handleFavoriteToggle = () => {
    if (!currentStation) return;

    if (isFavorite(currentStation.stationuuid)) {
      removeFavorite(currentStation);
      showToast('Removed from favorites', 'info');
    } else {
      addFavorite(currentStation);
      showToast('Added to favorites', 'success');
    }
  };

  const handleShare = () => {
    setShowShareModal(true);
  };

  if (!currentStation) return null;

  const isFavorited = currentStation ? isFavorite(currentStation.stationuuid) : false;

  return (
    <div className={`fixed bottom-0 left-0 right-0 ${isFairyTheme
      ? 'bg-purple-900/95 border-t border-purple-500/20'
      : isNeonTheme
        ? 'bg-black/95 border-t border-[#00FF00]/20'
        : 'bg-gray-900/95'
      } backdrop-blur-md text-white shadow-lg`}>

      {/* Progress Bar with error state */}
      <div className="h-0.5 w-full bg-gray-700">
        <div
          className={`h-full transition-all duration-500 ${streamError ? 'bg-red-500 w-full' :
            !isConnected ? 'bg-yellow-500 animate-pulse w-3/5' :
              isBuffering ? 'bg-yellow-500 w-full' :
                connectionQuality === 'good' ? 'bg-green-500 w-full' :
                  connectionQuality === 'fair' ? 'bg-yellow-500 w-full' :
                    'bg-red-500 w-full'
            }`}
        />
      </div>

      <div className="h-16 px-3 flex items-center justify-between relative">
        {/* Left Section: Station Image & Info */}
        <div className="flex items-center flex-1 min-w-0 max-w-[50%]">
          <div className="relative w-10 h-10 flex-shrink-0">
            <img
              src={currentStation.favicon || FALLBACK_IMAGE}
              alt={currentStation.name}
              className="w-full h-full rounded-md object-cover"
              onError={(e) => {
                const img = e.target as HTMLImageElement;
                img.src = FALLBACK_IMAGE;
              }}
            />
            {/* Connection Quality Indicator */}
            <div className={`absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full border-2 border-gray-900 ${!isConnected
              ? 'bg-red-500'
              : connectionQuality === 'good'
                ? 'bg-green-500'
                : connectionQuality === 'fair'
                  ? 'bg-yellow-500'
                  : 'bg-red-500'
              }`} />
          </div>
          <div className="ml-2 flex-1 min-w-0">
            <h3 className="font-medium text-sm truncate leading-tight max-w-full">
              <span className="inline-block truncate max-w-full" title={currentStation.name}>
                {currentStation.name}
              </span>
            </h3>
            <div className="flex items-center text-xs text-gray-300 space-x-1 truncate">
              {currentStation.countrycode && (
                <img
                  src={`https://flagcdn.com/w20/${currentStation.countrycode.toLowerCase()}.png`}
                  alt={currentStation.country}
                  className="inline-block w-3 h-3 rounded-sm flex-shrink-0"
                />
              )}
              <span className="truncate max-w-[60px]" title={currentStation.country}>{currentStation.country}</span>
              {streamError ? (
                <>
                  <span className="text-red-400 flex-shrink-0">
                    {unplayableReason === 'mixed-content' ? 'Insecure stream' : 'Unavailable'}
                  </span>
                  {externalUrl && (
                    <a
                      href={externalUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary underline flex-shrink-0"
                      title="Streams a secure page can't embed still play in their own tab"
                    >
                      Open
                    </a>
                  )}
                </>
              ) : (
                isConnected && !isBuffering && (
                  <>
                    <span className="text-green-500 flex-shrink-0">•</span>
                    <span className="text-green-500 animate-pulse flex-shrink-0">LIVE</span>
                  </>
                )
              )}
            </div>
          </div>
        </div>

        {/* Right Section: Controls */}
        <div className="flex items-center space-x-2">
          {/* Share Button */}
          <button
            onClick={handleShare}
            className={`p-2 rounded-full transition-colors ${isFairyTheme
              ? 'hover:bg-purple-800/60'
              : isNeonTheme
                ? 'hover:bg-[#00FF00]/20'
                : 'hover:bg-gray-700'
              }`}
          >
            <FaShare size={14} />
          </button>

          {/* Favorite Button */}
          <button
            onClick={handleFavoriteToggle}
            className={`p-2 rounded-full transition-colors ${isFavorited
              ? isFairyTheme ? 'text-purple-400' : isNeonTheme ? 'text-[#00FF00]' : 'text-primary'
              : 'text-gray-400'
              }`}
          >
            <FaHeart size={14} />
          </button>

          {/* Play/Pause Button */}
          <button
            onClick={handlePlayPause}
            className={`w-9 h-9 rounded-full flex items-center justify-center transition-colors ${isFairyTheme
              ? 'bg-purple-500 hover:bg-purple-600'
              : isNeonTheme
                ? 'bg-[#00FF00] hover:bg-[#00FF00]/90'
                : 'bg-primary hover:bg-primary/90'
              }`}
          >
            {isPlaying ? (
              <FaPause size={14} className={isNeonTheme ? 'text-black' : ''} />
            ) : (
              <FaPlay size={14} className={`ml-0.5 ${isNeonTheme ? 'text-black' : ''}`} />
            )}
          </button>

          {/* Volume Control */}
          <div className="relative" ref={volumeSliderRef}>
            <button
              onClick={handleVolumeClick}
              className={`p-2 rounded-full transition-colors ${isFairyTheme
                ? 'hover:bg-purple-800/60'
                : isNeonTheme
                  ? 'hover:bg-[#00FF00]/20'
                  : 'hover:bg-gray-700'
                }`}
            >
              {volume === 0 ? <FaVolumeMute size={14} /> : <FaVolumeUp size={14} />}
            </button>
            {showVolumeSlider && (
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 p-2 rounded-lg bg-gray-800 shadow-lg">
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={volume}
                  onChange={handleVolumeChange}
                  className="w-24 h-2"
                />
              </div>
            )}
          </div>

          {/* Sleep Timer Button */}
          <button
            onClick={() => setShowSleepTimer(true)}
            className={`p-2 rounded-full transition-colors flex items-center gap-1 ${isFairyTheme
              ? 'hover:bg-purple-800/60'
              : isNeonTheme
                ? 'hover:bg-[#00FF00]/20'
                : 'hover:bg-gray-700'
              } ${sleepTimer && sleepTimer > 0 ? 'animate-pulse' : ''}`}
            title={sleepCountdown ? `Sleeping in ${sleepCountdown}` : 'Sleep Timer'}
          >
            <FaMoon
              size={14}
              className={sleepTimer && sleepTimer > 0
                ? isNeonTheme
                  ? 'text-[#00FF00]'
                  : 'text-primary'
                : isNeonTheme
                  ? 'text-[#00FF00]'
                  : ''
              }
            />
            {sleepCountdown && (
              <span className={`text-[10px] font-semibold tabular-nums ${isNeonTheme ? 'text-[#00FF00]' : 'text-primary'}`}>
                {sleepCountdown}
              </span>
            )}
          </button>
        </div>

        {/* Sleep Timer Modal */}
        {showSleepTimer && (
          <SleepTimer onClose={() => setShowSleepTimer(false)} />
        )}

        {/* Share Modal */}
        {showShareModal && (
          <ShareModal station={currentStation} onClose={() => setShowShareModal(false)} />
        )}

        {/* Audio Player Logic */}
        <MobilePlayer />
      </div>
    </div>
  );
};

export default MobilePlayerBar;